(() => {
  'use strict';

  const QUERY = `query F2WCountryDirectory($after: String, $language: LanguageCode!) @inContext(language: $language) {
    metaobjects(type: "country", first: 100, after: $after) {
      nodes { id onlineStoreUrl name: field(key: "name") { value } code: field(key: "code") { value } }
      pageInfo { hasNextPage endCursor }
    }
  }`;

  const nativeUrl = (value) => {
    if (!value) return '';
    try {
      const url = new URL(String(value), window.location.origin);
      if (!['http:', 'https:'].includes(url.protocol)) return '';
      const root = window.Shopify?.routes?.root || '/';
      const prefix = root === '/' ? '' : root.replace(/\/$/, '');
      let path = url.pathname;
      if (/^\/[a-z]{2}(\/|$)/i.test(path)) path = path.replace(/^\/[a-z]{2}/i, '');
      if (prefix && (path === prefix || path.startsWith(`${prefix}/`))) path = path.slice(prefix.length);
      return `${prefix}${path}${url.search}${url.hash}`;
    } catch (_) { return ''; }
  };

  const normalize = (value) => String(value || '').toLocaleLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();

  document.querySelectorAll('[data-f2w-country-directory]').forEach((root) => {
    if (root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';
    const list = root.querySelector('[data-f2w-country-results]');
    const europeTrack = root.querySelector('[data-f2w-secondary-markets]');
    const europeOther = root.querySelector('[data-f2w-europe-other]');
    const input = root.querySelector('[data-f2w-country-search]');
    const status = root.querySelector('[data-f2w-country-status]');
    const empty = root.querySelector('[data-f2w-country-empty]');
    if (!list || !input || !status || !empty) return;
    const endpoint = `https://${root.dataset.shopDomain}/api/${root.dataset.apiVersion}/graphql.json`;
    const token = root.dataset.storefrontToken || '';
    const language = (root.dataset.language || 'nl').split('-')[0].toUpperCase();
    let countries = [];
    let allCountries = [];
    const countFormatter = new Intl.NumberFormat(root.dataset.language || 'nl');
    root.querySelectorAll('[data-f2w-countries-snapshot-value]').forEach((node) => {
      const value = Number(node.textContent.trim());
      if (Number.isFinite(value)) node.textContent = countFormatter.format(value);
    });
    // European geographic and sporting context; overseas territories stay in the world list.
    const europeanCodes = new Set('AL AD AM AT AZ BA BE BG BY CH CY CZ DE DK EE ES FI FR GB GB-ENG GB-SCT GB-WLS GE GR HR HU IE IS IT JE LI LT LU LV MC MD ME MK MT NL NO PL PT RO RS RU SE SI SK SM TR UA VA XK'.split(' '));
    const activeMarketCodes = new Set(String(root.dataset.marketCountryCodes || 'AT,BE,CZ,DE,DK,EE,ES,FI,FR,GR,IE,IT,LT,LU,LV,MT,NL,NO,PL,PT,SE').split(',').map((code) => code.trim().toUpperCase()).filter(Boolean));
    const makeEuropeCard = (item) => {
      const link = document.createElement('a');
      link.className = 'f2w-fightbase-hub__sport-card';
      link.href = item.url;
      link.dataset.f2wMarketCode = item.code;
      const placeholder = document.createElement('span');
      placeholder.className = 'f2w-fightbase-hub__sport-card-placeholder';
      placeholder.setAttribute('aria-hidden', 'true');
      placeholder.textContent = item.code || item.name.slice(0, 1);
      const body = document.createElement('span');
      body.className = 'f2w-fightbase-hub__sport-card-body';
      const title = document.createElement('strong');
      title.className = 'f2w-fightbase-hub__sport-card-title';
      title.textContent = item.name;
      body.append(title);
      link.append(placeholder, body);
      return link;
    };
    const makeFlag = (code) => {
      const normalized = String(code || '').trim().toLowerCase();
      if (!/^[a-z]{2}$/.test(normalized) && !['gb-eng','gb-sct','gb-wls'].includes(normalized)) return null;
      const wrapper = document.createElement('span');
      wrapper.className = 'f2w-country-flag';
      wrapper.setAttribute('aria-hidden', 'true');
      const image = document.createElement('img');
      image.src = 'https://flagcdn.com/w40/' + normalized + '.png';
      image.srcset = 'https://flagcdn.com/w80/' + normalized + '.png 2x';
      image.alt = '';
      image.width = 20;
      image.height = 15;
      image.loading = 'lazy';
      image.decoding = 'async';
      image.addEventListener('error', () => wrapper.remove(), { once:true });
      wrapper.append(image, document.createTextNode('\u00a0'));
      return wrapper;
    };

    const makeFlagChip = (item) => {
      const link = document.createElement('a');
      link.className = 'f2w-event-card__sport';
      link.href = item.url;
      link.textContent = item.name;
      const flag = makeFlag(item.code);
      if (flag) link.prepend(flag);
      return link;
    };

    const render = () => {
      const q = normalize(input.value);
      const matches = (q ? allCountries : countries).filter((item) => normalize(item.name).includes(q) || normalize(item.code).includes(q));
      const fragment = document.createDocumentFragment();
      const definitions = [
        ['europe', europeanCodes],
        ['africa', new Set('DZ AO BJ BW BF BI CV CM CF TD KM CG CD CI DJ EG GQ ER SZ ET GA GM GH GN GW KE LS LR LY MG MW ML MR MU YT MA MZ NA NE NG RE RW SH ST SN SC SL SO ZA SS SD TZ TG TN UG EH ZM ZW'.split(' '))],
        ['asia', new Set('AF BH BD BT BN KH CN HK IN ID IR IQ IL JP JO KZ KW KG LA LB MO MY MV MN MM NP KP OM PK PS PH QA SA SG KR LK SY TW TJ TH TL TM AE UZ VN YE'.split(' '))],
        ['northAmerica', new Set('AI AG AW BS BB BZ BM BQ CA KY CR CU CW DM DO SV GL GD GP GT HT HN JM MQ MX MS NI PA PR BL KN LC MF PM VC SX TT TC US VG VI'.split(' '))],
        ['southAmerica', new Set('AR BO BR CL CO EC FK GF GY PY PE SR UY VE'.split(' '))],
        ['oceania', new Set('AS AU CK FJ PF GU KI MH FM NR NC NZ NU NF MP PW PG PN WS SB TK TO TV VU WF'.split(' '))]
      ];
      const groups = new Map([...definitions.map(([key]) => [key, []]), ['other', []]]);
      matches.forEach((item) => {
        const key = definitions.find(([, codes]) => codes.has(String(item.code).toUpperCase()))?.[0] || 'other';
        groups.get(key).push(item);
      });
      groups.forEach((items, key) => {
        if (!items.length) return;
        const section = document.createElement('section');
        const header = document.createElement('div');
        header.className = 'f2w-fightbase-hub__sports-header';
        const heading = document.createElement('h3');
        heading.textContent = root.dataset['group' + key.charAt(0).toUpperCase() + key.slice(1)] || '';
        header.append(heading);
        const links = document.createElement('div');
        links.className = 'f2w-detail__pills';
        items.forEach((item) => links.append(makeFlagChip(item)));
        section.append(header, links);
        fragment.append(section);
      });
      list.replaceChildren(fragment);
      empty.hidden = matches.length !== 0;
    };
    input.addEventListener('input', render);

    (async () => {
      try {
        if (!token) throw new Error('Missing Storefront token');
        let after = null;
        let more = true;
        const seen = new Set();
        while (more) {
          const response = await fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Shopify-Storefront-Access-Token': token },
            body: JSON.stringify({ query: QUERY, variables: { after, language } })
          });
          if (!response.ok) throw new Error(`Country request failed: ${response.status}`);
          const payload = await response.json();
          if (payload.errors?.length || !payload.data?.metaobjects?.pageInfo) throw new Error('Country result unavailable');
          const connection = payload.data.metaobjects;
          for (const node of connection.nodes || []) {
            const name = node?.name?.value?.trim();
            const url = nativeUrl(node?.onlineStoreUrl);
            if (!node?.id || seen.has(node.id) || !name || !url) continue;
            seen.add(node.id);
            countries.push({ name, code: node?.code?.value || '', url });
          }
          more = connection.pageInfo.hasNextPage === true;
          if (more && (!connection.pageInfo.endCursor || connection.pageInfo.endCursor === after)) throw new Error('Country pagination stopped');
          after = connection.pageInfo.endCursor;
        }
        const featuredCodes = new Set(Array.from(root.querySelectorAll('[data-f2w-featured-markets] [data-f2w-market-code]'), (link) => String(link.dataset.f2wMarketCode || '').toUpperCase()));
        const existingEurope = new Map(Array.from(europeTrack?.querySelectorAll('[data-f2w-market-code]') || [], (link) => [String(link.dataset.f2wMarketCode || '').toUpperCase(), link]));
        const european = countries.filter((item) => europeanCodes.has(String(item.code).toUpperCase()) && !featuredCodes.has(String(item.code).toUpperCase()));
        const order = (a, b) => a.name.localeCompare(b.name, root.dataset.language || 'nl', { sensitivity: 'base' });
        const marketEurope = european.filter((item) => activeMarketCodes.has(String(item.code).toUpperCase())).sort(order);
        const otherEurope = european.filter((item) => !activeMarketCodes.has(String(item.code).toUpperCase())).sort(order);
        if (europeTrack) europeTrack.replaceChildren(...marketEurope.map((item) => existingEurope.get(String(item.code).toUpperCase()) || makeEuropeCard(item)));
        if (europeOther) {
          europeOther.replaceChildren(...otherEurope.map(makeFlagChip));
          europeOther.hidden = otherEurope.length === 0;
        }
        allCountries = countries.slice().sort(order);
        countries = countries.filter((item) => !europeanCodes.has(String(item.code).toUpperCase()) && !featuredCodes.has(String(item.code).toUpperCase()));
        countries.sort((a, b) => a.name.localeCompare(b.name, root.dataset.language || 'nl', { sensitivity: 'base' }));
        input.disabled = false;
        list.hidden = false;
        status.hidden = true;
        render();
      } catch (error) {
        status.textContent = root.dataset.errorLabel || '';
        console.warn('[FightBase Countries] Directory unavailable', error);
      }
    })();
  });
})();



